import { GRID_TREE_DATA_GROUPING_FIELD, GridColDef, GridRenderCellParams } from '@mui/x-data-grid-pro';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Trans } from 'react-i18next';
import { SampleTrackingCountRow, SampleTrackingCounts, TransitionType } from '../../data/SampleTrackingData';
import { GridCellCallBack } from '../../components/grid/GridCellCallback';
import {
  SampleTrackingSelectedTransitionSampleModal,
  SampleTrackingTransitionSampleModal,
} from './SampleTrackingTransitionSampleModal';
import { generateWidthFromProperty } from '../../util/grid/TableUtils';
import { all, sampleGroup, selectedSampleCount } from '../../util/Constants';
import { LoadingState, LoadState } from '../../components/LoadingStateUtil';
import { CaseInsensitiveMap, createCaseInsensitiveMap, UseStateSetter } from '../../util/TypeUtil';
import useMemoTranslation from '../../hooks/UseMemoTranslation';
import { Box, Typography, Divider } from '@mui/material';
import { CompactGridWrapper } from '../../components/grid/CompactGridWrapper';
import { TransitionTableHeaderIcon } from '../../util/TransitionUtil';
import { orderBy } from 'lodash';
import { SampleTrackingPathConfigurationModal } from './SampleTrackingPathConfigurationModal';
import { FlexBox } from '../../components/FlexBox';
import { LightTooltip } from '../../components/grid/cell/GridCellTooltip';
import { AuthorizedSection } from '../../auth/AuthorizedSection';
import { useConfiguredTransitions } from '../../components/hooks/UseConfiguredTransitions';
import { ConfiguredTransition } from '../../data/ConfiguredTransitionData';
import { CustomizableSwitch } from '../../components/CustomizableSwitch';
import { useSampleTrackingContext } from '../../contexts/SampleTrackingContext';

export interface SampleTrackingGridProps {
  researchProjectId: string;
}

export type TransitionTypeExtended = TransitionType | 'Selected';

interface TransitionRecordModalProps {
  open: boolean;
  title?: string;
  transitionType?: TransitionTypeExtended;
  sampleGroup?: string;
  configuredTransitionId?: string;
}

type Row = {
  id: number;
  sampleGroup: string;
  hierarchy: string[];
  sampleCounts: CaseInsensitiveMap<string, SampleTrackingCountRow>;
  patientCounts: CaseInsensitiveMap<string, SampleTrackingCountRow>;
};

export const SampleTrackingGrid = ({ researchProjectId }: SampleTrackingGridProps) => {
  const { t } = useMemoTranslation();
  const { getSampleTrackingCountsForProject, refreshSampleTrackingCountsForProject } = useSampleTrackingContext();

  const [refreshKpiTrigger, setRefreshKpiTrigger] = useState<boolean>(false);
  const [loadingState, setLoadingState] = useState<LoadingState>({ status: 'NotStarted' });
  const [showHistory, setShowHistory] = useState<boolean>(false);

  const [transitionRecordModalProps, setTransitionRecordModalProps] = useState<TransitionRecordModalProps>({
    open: false,
  });

  const [data, setData] = useState<ReadonlyArray<SampleTrackingCounts>>([]);
  const [configuredTransitions, configuredTransitionsRefreshTrigger] = useConfiguredTransitions(
    researchProjectId,
    setLoadingState
  );

  const columns = useColumns(setTransitionRecordModalProps, configuredTransitions, showHistory);
  const rows = useRows(data);
  const projectId = useMemo(() => researchProjectId, [researchProjectId]);
  const fetchingData = useRef(false);

  // Fetches the sample tracking counts for the project from the context.
  useEffect(() => {
    if (projectId === undefined) {
      return;
    }

    return LoadState(setLoadingState, async () => {
      if (data.length === 0 && !fetchingData.current && projectId) {
        fetchingData.current = true;
        const existingData = await getSampleTrackingCountsForProject(projectId);

        if (existingData) {
          setData(existingData);
        }
        fetchingData.current = false;
      }
    });
  }, [data, projectId, getSampleTrackingCountsForProject]);

  // Tells the context to refresh the KPI data for the project.
  // This is typically triggered when an action is performed on a transition.
  useEffect(() => {
    if (refreshKpiTrigger && projectId) {
      return LoadState(setLoadingState, async () => {
        setRefreshKpiTrigger(false);
        const newData = await refreshSampleTrackingCountsForProject(projectId);
        setData(newData);
      });
    }
  }, [refreshKpiTrigger, projectId, refreshSampleTrackingCountsForProject]);

  return (
    <>
      <>
        <Box display='flex' flexDirection='row' alignItems='center' mb={1}>
          <Typography variant='subtitle1'>{t('sampleTrackingGridInfo')}</Typography>
          <Box display='flex' flexDirection='row' alignItems='center' ml='auto' gap={1}>
            <CustomizableSwitch
              title={t('showHistory')}
              checked={showHistory}
              onChange={() => setShowHistory(!showHistory)}
            />
            <AuthorizedSection hasSampleTrackingWriteAccess>
              <Box display='flex' flexDirection='row' alignItems='center'>
                <SampleTrackingPathConfigurationModal
                  onClose={hasChanges => {
                    if (hasChanges) {
                      configuredTransitionsRefreshTrigger();
                    }
                  }}
                />
              </Box>
            </AuthorizedSection>
          </Box>
        </Box>
        <CompactGridWrapper
          loadingState={loadingState}
          rows={rows}
          columns={columns}
          disableColumnFilter={true}
          disableColumnMenu={true}
          initialState={{
            pinnedColumns: { left: [GRID_TREE_DATA_GROUPING_FIELD] },
            sorting: {
              sortModel: [{ field: GRID_TREE_DATA_GROUPING_FIELD, sort: 'asc' }],
            },
          }}
          hideFooterSelectedRowCount={true}
          getTreeDataPath={row => row?.hierarchy}
          treeData
          groupingColDef={{
            headerAlign: 'center',
            align: 'center',
            headerName: t(sampleGroup),
            width: generateWidthFromProperty(data ?? [], 150, (row: SampleTrackingCounts) => row.sampleGroup),
          }}
          disableRowSelectionOnClick
          getRowHeight={() => 'auto'}
          columnHeaderHeight={75}
        />
        {transitionRecordModalProps.open && (
          <>
            {transitionRecordModalProps.transitionType === 'Selected' ? (
              <SampleTrackingSelectedTransitionSampleModal
                researchProjectId={researchProjectId}
                title={transitionRecordModalProps.title ?? ''}
                sampleGroup={transitionRecordModalProps.sampleGroup}
                transitionType={transitionRecordModalProps.transitionType}
                onClose={() => {
                  setTransitionRecordModalProps({ open: false });
                }}
              />
            ) : (
              <SampleTrackingTransitionSampleModal
                researchProjectId={researchProjectId}
                title={transitionRecordModalProps.title ?? ''}
                sampleGroup={transitionRecordModalProps.sampleGroup}
                transitionType={transitionRecordModalProps.transitionType}
                configuredTransitionId={transitionRecordModalProps.configuredTransitionId ?? ''}
                setRefreshKpiTrigger={setRefreshKpiTrigger}
                onClose={() => {
                  setTransitionRecordModalProps({ open: false });
                }}
              />
            )}
          </>
        )}
      </>
      <Typography variant={'subtitle1'} mt={2}>
        <Trans t={t} i18nKey='countByLegend' />
      </Typography>
    </>
  );
};

const useRows = (sampleTrackingBySampleTypes: ReadonlyArray<SampleTrackingCounts>) => {
  const { t } = useMemoTranslation();

  return useMemo(() => {
    let index = 0;

    const rows: Row[] = sampleTrackingBySampleTypes.map(row => {
      return {
        id: index++,
        hierarchy: row.sampleGroup?.toLowerCase() === 'all' ? [t(all)] : [t(all), row.sampleGroup],
        patientCounts: createCaseInsensitiveMap(row.patientCounts),
        sampleCounts: createCaseInsensitiveMap(row.sampleCounts),
        sampleGroup: row.sampleGroup,
      };
    });

    return rows;
  }, [t, sampleTrackingBySampleTypes]);
};

const useColumns = (
  setTransitionRecordModalProps: UseStateSetter<TransitionRecordModalProps>,
  configuredTransitions: ReadonlyArray<ConfiguredTransition>,
  showHistory: boolean
): GridColDef[] => {
  const { t } = useMemoTranslation();

  const maxTransitionDisplayNameLength = configuredTransitions
    .map(c => c.displayName)
    .reduce((max, current) => Math.max(max, current.length), 0);
  const minWidth = maxTransitionDisplayNameLength > 20 ? 150 : 100;
  const maxWidth = 300;

  const gridCellRendererHelper = useCallback(
    (transitionType: TransitionTypeExtended, configuredTransitionId: string, displayName: string, text?: string) => {
      return (params: GridRenderCellParams) => {
        const sampleCounts = params.row?.sampleCounts?.get(displayName) ?? 0;
        const patientCounts = params.row?.patientCounts?.get(displayName) ?? 0;

        const sampleGroup = params.row?.sampleGroup === all ? undefined : params.row?.sampleGroup;

        const items =
          transitionType === 'Selected'
            ? [
                {
                  label: 'S:',
                  fullLabel: t('selected'),
                  count: `${sampleCounts?.remainingCount ?? 0} (${patientCounts?.remainingCount ?? 0}p)`,
                },
              ]
            : [
                {
                  label: 'N:',
                  fullLabel: t('needsCheckBy'),
                  count: `${sampleCounts?.workLeftCount ?? 0}`,
                },
                {
                  label: 'P:',
                  fullLabel: t('passed'),
                  count:
                    showHistory && sampleCounts?.passedCompletedCount
                      ? `${sampleCounts?.passedCount ?? 0} (${sampleCounts?.passedCompletedCount ?? 0})`
                      : `${sampleCounts?.passedCount ?? 0}`,
                },
                {
                  label: 'F:',
                  fullLabel: t('failed'),
                  count:
                    showHistory && sampleCounts?.failedCompletedCount
                      ? `${sampleCounts?.failedCount ?? 0} (${sampleCounts?.failedCompletedCount ?? 0})`
                      : `${sampleCounts?.failedCount ?? 0}`,
                },
                {
                  label: 'R:',
                  fullLabel: t('remaining'),
                  count: `${sampleCounts?.remainingCount ?? 0}`,
                },
                {
                  label: 'E:',
                  fullLabel: t('exited'),
                  count: `${sampleCounts?.completedCount ?? 0}`,
                },
                {
                  label: 'T:',
                  fullLabel: t('total'),
                  count: `${sampleCounts?.totalCount ?? 0}`,
                },
              ];

        return (
          <FlexBox flexDirection={'column'} paddingTop={1} paddingBottom={1}>
            {items.map((item, i) => (
              <React.Fragment key={i}>
                {item.label === 'E:' && <Divider sx={{ bgcolor: 'black' }} />}
                <FlexBox sx={{ gap: 0.5, alignItems: 'center' }}>
                  <Typography variant={'h6'} sx={{ fontSize: '12px', fontWeight: 'bold', width: '30%' }}>
                    <LightTooltip title={item.fullLabel}>
                      <span>{item.label}</span>
                    </LightTooltip>
                  </Typography>
                  <GridCellCallBack
                    text={item.count === '0' || item.count === '0 (0p)' ? '--' : item.count}
                    textStyle={{ fontWeight: 'bold' }}
                    callBack={() =>
                      setTransitionRecordModalProps({
                        open: true,
                        title: `'${displayName}' Samples (${sampleGroup ?? t(all)})`,
                        transitionType: transitionType,
                        sampleGroup: sampleGroup,
                        configuredTransitionId: configuredTransitionId,
                      })
                    }
                    sx={{ padding: 0 }}
                  />
                </FlexBox>
              </React.Fragment>
            ))}
          </FlexBox>
        );
      };
    },
    [t, setTransitionRecordModalProps, showHistory]
  );

  return useMemo(() => {
    const uniqueTransitions = new Map<string, ConfiguredTransition>();
    const columns: GridColDef[] = [
      {
        field: selectedSampleCount,
        renderHeader: () => <TransitionTableHeaderIcon transitionType={'Selected'} header={t(selectedSampleCount)} />,
        headerAlign: 'center',
        align: 'center',
        minWidth: minWidth,
        maxWidth: maxWidth,
        flex: 1,
        renderCell: gridCellRendererHelper('Selected', 'idk', 'Selected'),
      },
    ];

    orderBy(configuredTransitions, o => o.transitionOrder).forEach(transition => {
      if (!uniqueTransitions.has(transition.configuredTransitionId)) {
        uniqueTransitions.set(transition.configuredTransitionId, transition);

        const transitionType = transition.transitionEnum.name as TransitionType;

        columns.push({
          field: transition.displayName,
          headerName: transition.displayName,
          renderHeader: () => (
            <TransitionTableHeaderIcon transitionType={transitionType} header={transition.displayName} />
          ),
          headerAlign: 'center',
          align: 'center',
          minWidth: minWidth,
          maxWidth: maxWidth,
          flex: 1,
          renderCell: gridCellRendererHelper(transitionType, transition.configuredTransitionId, transition.displayName),
        });
      }
    });

    return columns;
  }, [t, gridCellRendererHelper, configuredTransitions, minWidth]);
};
